import { useEffect, type RefObject } from 'react'
import { observer } from 'mobx-react-lite'
import cn from 'classnames'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { PHONE_QUERY } from '@/utils/breakpoints'
import { useMediaQuery } from '@/utils/hooks/useMediaQuery'
import { Routes } from '@/utils/navigation/routes'
import { rememberOnboardingDoor } from '@/utils/onboardingEntry'

import { useLandingStickyCtaStore } from './LandingStickyCtaStore'
import styles from './LandingStickyCta.module.scss'

type LandingStickyCtaProps = {
  /** The hero's row of buttons: the bar comes up once it has scrolled above the screen. */
  heroRef: RefObject<HTMLElement | null>
  /** The closing block: the bar goes down as soon as it is on screen, and stays down below it. */
  closingRef: RefObject<HTMLElement | null>
}

/**
 * The hero's ask, pinned to the bottom of a phone between the hero and the closing block. When it is
 * up and why: LandingStickyCtaStore.ts.
 *
 * Phone only, and not mounted at all above it: a desktop window holds the nav's own button on every
 * screen of the page, and the desktop must not change by a pixel.
 *
 * The same string, target and door as the hero's button, because it IS the hero's button, followed
 * down the page. It does not register with useBottomUiHeight (Home/support/bottomUi.ts): that height
 * lifts the round help button, and the landing has none.
 */
export const LandingStickyCta = ({ heroRef, closingRef }: LandingStickyCtaProps) => {
  const phone = useMediaQuery(PHONE_QUERY)
  return phone ? <StickyBar heroRef={heroRef} closingRef={closingRef} /> : null
}

const StickyBar = observer(({ heroRef, closingRef }: LandingStickyCtaProps) => {
  const { t } = useTranslation()
  const store = useLandingStickyCtaStore()

  useEffect(() => {
    const hero = heroRef.current
    const closing = closingRef.current
    return hero && closing ? store.watch(hero, closing) : undefined
  }, [store, heroRef, closingRef])

  return (
    /* Hidden, not unmounted: it slides in and out. `inert` keeps the hidden link out of the tab order
       and away from a screen reader, which `visibility` alone would do only after the slide ends. */
    <div className={cn(styles.root, { [styles.shown as string]: store.visible })} inert={!store.visible}>
      <Link className={styles.button} to={Routes.onboard} onClick={() => rememberOnboardingDoor('hero')}>
        {t('landing.cta.primary')}&nbsp;&nbsp;→
      </Link>
    </div>
  )
})
